import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyDataGrid,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from "react";
import { Link } from "wouter";
import type { RunTransformModelResult, RunTransformTestResult } from "@shared/types/runs";
import type { DbtPhaseTiming, DbtJobRunRow } from "@shared/types/transformations";
import { dbtNodeRows } from "@shared/types/dbtCauses";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import { LoadingRows } from "@/components/access/ScreenStates";
import { NOT_STARTED_LABEL, formatDuration } from "@/components/runs/runFormatting";
import { StartedByCell, StartedCell, isKnownSeconds } from "@/components/runs/RunsTable";
import { StatusCell } from "@/components/runs/StatusCell";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { usePermissions } from "@/hooks/use-permissions";
import { RUN_POLL_INTERVAL_MS, usePollWhile, useRunTransformResults } from "@/hooks/use-runs";
import {
  DBT_JOB_RUNS_PAGE_SIZES,
  useRefreshFirstRunPage,
  useDbtJobRun,
  useDbtJobRunPages,
} from "@/hooks/use-dbt-jobs";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { ChevronDownIcon, ChevronRightIcon, CopyIcon, LogsIcon, RetryIcon } from "@/lib/icons";
import { ROUTES, absolutePath } from "@/lib/routes";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { isRunInFlight, phasePhrase, runOutcome } from "@/lib/transformations/runOutcome";
import { CauseBlock, failuresUnit } from "./CauseBlock";
import { Dash, ROWS_HINT, lastResultWord } from "./TransformationModelsView";
import { testKindWord } from "./TransformationTestsView";
import {
  SOURCE_SQL_TENANT_ADMIN_ONLY,
  TransformationSqlDialog,
  type TransformationSqlTarget,
} from "./TransformationSqlDialog";
import {
  formatRows,
  formatSeconds,
  runNotStarted,
  runStartOf,
  sumRowCounts,
} from "./transformFormatting";
import {
  modelsEmptyMessage,
  testsEmptyMessage,
  type EmptyResultsFacts,
} from "@/lib/transformations/emptyResults";

/**
 * The Transformations landing's History view (Session 11, ux-s11-transformations.md §3.3; Studio's
 * Run History drawer, shots 2 to 4, as a view with an address).
 *
 * Reads `GET /dbt-jobs/runs` 20 at a time; Load more reads the next page by the server's keyset
 * cursor (round 7; before, it widened to 50 and 100 and stopped there). The first page alone is
 * re-read every 5 s ONLY while the newest row is in flight, one read at a time; a write's
 * invalidation re-reads every loaded page. A row whose selection the list
 * shortened reads its whole row (`GET /dbt-jobs/runs/:runId`) once its details are open. Expanding a run reads its
 * `GET /runs/:id/transform-results` once; a collapsed run reads nothing. Neither read is audited.
 * Query opens the audited SQL read, on a click only (models and tests; a seed or snapshot has
 * none). Viewer-safe: nothing here writes.
 */

export interface TransformationHistoryViewProps {
  canStart: boolean;
}

/**
 * Round 3 (owner, 2026-09-30, 7ipw67.png): the grid was percentages only, the copy icon sat on the
 * run id and "Started by" was cut with nothing on mouse over. The columns a value sizes have pixel
 * widths, measured in Chromium in both fonts (client/AGENTS.md), border-box with the cell's 24 px
 * padding: Details holds its header (96 px); Run the id's first 24 characters (173 px at 12 px
 * monospace) beside the 4 px gap and the 28 px copy button, so the id is two lines at most;
 * Started its time on two lines at the comma over "took 59m 59s"; Status its header and the
 * widest outcome pill. The rest share what is left in proportion to a minimum that holds their
 * header (Transformations, 180 px) and, for Tests, a count line that may wrap.
 *
 * gridsfit (lead, 2026-09-30): the 1256 px floor scrolled at 1440 px. The duration reads under the
 * start (the Runs list's pairing), Run keeps 232 px and Tests 120: the floor, 1128 px, fits the
 * 1131 px pane at 1440 px. Below it (1280 px) the grids scroll under a pinned Status
 * (`HistorySegmentGrid`), with the run, its start and its transformations in view.
 */
const DETAILS_WIDTH = 96;
const RUN_ID_WIDTH = 232;
const STARTED_WIDTH = 128;
const STATUS_WIDTH = 128;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  startedBy: 136,
  dbtJobNames: 184,
  modelsReported: 104,
  tests: 120,
};

/** Round 7: the runs one page holds; Load more reads the next page by cursor. */
const PAGE_SIZE: number = DBT_JOB_RUNS_PAGE_SIZES[0];

/**
 * Review S1 (UX §3.3, "a `bg-muted` region under the row"): the grid has no row expansion, so the
 * runs are cut into consecutive grids that each END at an expanded run, and that run's region
 * follows its grid. Each segment is keyed by its first run, so the grid holding a toggled row is
 * the same element before and after, and the toggle keeps its focus.
 */
export function historySegments(
  rows: DbtJobRunRow[],
  expanded: readonly string[],
): DbtJobRunRow[][] {
  const segments: DbtJobRunRow[][] = [];
  let current: DbtJobRunRow[] = [];
  for (const row of rows) {
    current.push(row);
    if (expanded.includes(row.runId)) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/**
 * "{built} of {selected} built" (+ failed, + skipped), from the step's own counts. Round 6, T5:
 * built is the models that succeeded; a row from before the four counts keeps reported minus
 * failed (`dbtModelCountsOf`).
 */
function modelsCell(row: DbtJobRunRow): { text: string; failed: number; skipped: number } | null {
  const counts = dbtModelCountsOf(row);
  if (!counts) return null;
  return {
    text: `${counts.built} of ${counts.selected} built`,
    failed: counts.failed,
    skipped: counts.skipped,
  };
}

/**
 * "{p} passed · {f} failed · {w} warned", terse for a narrow cell. With no test (#1083): "Tests
 * off" only when the run's dbt pass asked for none (`testsRequested === false`, or a row from
 * before the field that counted none), "Stopped" for a failed or cancelled dbt step, "Skipped"
 * for a skipped one, "Not run" when tests were asked for and none ran, else a dash. The whole
 * sentence is the expanded run's (`emptyResults.ts`).
 */
export function testsLine(row: DbtJobRunRow): string {
  if (row.testsRequested === false) return "Tests off";
  const noCounts = row.testsSucceeded === null && row.testsFailed === null;
  if (row.testsTotal === 0 || noCounts) {
    if (row.stepStatus === "failed" || row.stepStatus === "cancelled") return "Stopped";
    if (row.stepStatus === "skipped") return "Skipped";
    if (row.testsTotal === 0) return row.testsRequested === null ? "Tests off" : "Not run";
    return "—";
  }
  const warned = row.testsWarned ?? 0;
  const passed = row.testsSucceeded === null ? null : Math.max(0, row.testsSucceeded - warned);
  return `${passed ?? "—"} passed · ${row.testsFailed ?? 0} failed · ${warned} warned`;
}

/** A phase's seconds as the Setup line says them: `0.4 s`, `4 s`, `12 s`, `1m 30s`. */
function phaseSeconds(seconds: number): string {
  if (seconds < 10) return `${Number(seconds.toFixed(1))} s`;
  if (seconds < 60) return `${Math.round(seconds)} s`;
  return formatDuration(seconds);
}

/** The note after a phase: where the setup stopped, or that it was skipped. */
function phaseMark(outcome: DbtPhaseTiming["outcome"]): string {
  if (outcome === "failed") return " (stopped here)";
  return outcome === "skipped" ? " (skipped)" : "";
}

/** "config 0.4 s · auth 1.1 s · … · {total} total", the stop marked "stopped here". */
export function setupLine(phases: readonly DbtPhaseTiming[]): string {
  const parts = phases.map((phase) => {
    const mark = phaseMark(phase.outcome);
    return `${phase.name} ${phaseSeconds(phase.seconds)}${mark}`;
  });
  const total = phases.reduce((sum, phase) => sum + phase.seconds, 0);
  return [...parts, `${phaseSeconds(total)} total`].join(" · ");
}

const SQL_KINDS = /^(model|test|unit_test)\./;

/** The whole selection, that it could not be read, or that it is being read. */
function selectionText(select: string | null | undefined, failed: boolean): ReactElement {
  if (select) {
    return (
      <SkyText size={200} font="monospace" block className="break-all">
        {select}
      </SkyText>
    );
  }
  if (failed) {
    return (
      <SkyText size={200} color="muted">
        We could not read the whole selection.
      </SkyText>
    );
  }
  return (
    <SkyText size={200} color="muted" role="status">
      Reading the whole selection…
    </SkyText>
  );
}

/**
 * Round 7 ("Keep the dbt run list small with long chosen-model selections"): the whole selection
 * of a run whose list row carried a preview, from `GET /dbt-jobs/runs/:runId`.
 */
function WholeSelection({
  row,
  whole,
  regionId,
}: {
  row: DbtJobRunRow;
  whole: ReturnType<typeof useDbtJobRun>;
  regionId: string;
}) {
  const select = whole.data ? (whole.data.adhocSelect ?? whole.data.select) : null;
  const count = row.chosenModelCount ?? whole.data?.chosenModelCount;
  return (
    <SkyCard className="bg-card p-3" testId={`${regionId}-selection`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <SkyText size={300} weight="semibold">
          Selection
        </SkyText>
        {count !== undefined ? (
          <SkyText size={200} color="muted">
            {`${count} ${count === 1 ? "model" : "models"} chosen`}
          </SkyText>
        ) : null}
      </div>
      {selectionText(select, whole.isError)}
    </SkyCard>
  );
}

type RunOutcome = ReturnType<typeof runOutcome>;

function noModelsSentence(outcome: RunOutcome, row: DbtJobRunRow): string {
  // #1207 round 1: the phase in words ("cloning the repository"), never dbt's token.
  if (outcome.kind === "stopped" && outcome.phase) {
    return `No model ran. The run stopped while ${phasePhrase(outcome.phase)}.`;
  }
  // #1083: the run page's rule ("yet" only while the run is active), from one helper.
  return modelsEmptyMessage(historyFacts(row));
}

/** #1083: a History row's facts for the empty sentences (`emptyResults.ts`). */
function historyFacts(row: DbtJobRunRow): EmptyResultsFacts {
  return {
    runStatus: row.runStatus,
    transformStatus: row.stepStatus,
    skippedReason: row.skippedReason,
    testsTotal: row.testsTotal,
    modelsReported: row.modelsReported,
    transformStarted: row.startedAt !== null,
  };
}

/** "Setup: …": the phase times, or why there are none. */
function setupText(row: DbtJobRunRow, outcome: RunOutcome): string {
  if (row.phases && row.phases.length > 0) return setupLine(row.phases);
  // #1207 round 1 (#1191 rule): no word about images.
  return outcome.kind === "stopped" && outcome.phase
    ? `Stopped while ${phasePhrase(outcome.phase)}. No step times were recorded for this run.`
    : "No step times were recorded for this run.";
}

/** The Tests card's body: no per-test results, all passed, or the failed and warned tests. */
function RunTestsBody({
  row,
  regionId,
  testCount,
  testsRequested,
  problemTests,
  testColumns,
}: {
  row: DbtJobRunRow;
  regionId: string;
  testCount: number;
  /** The server's `testsRequested` for this run (#1083); undefined until it answers. */
  testsRequested: boolean | null | undefined;
  problemTests: RunTransformTestResult[];
  testColumns: SkyDataGridColumn<RunTransformTestResult>[];
}) {
  if (testCount === 0) {
    return (
      <SkyText size={200} color="muted">
        {testsEmptyMessage(testsRequested ?? row.testsRequested, historyFacts(row))}
      </SkyText>
    );
  }
  if (problemTests.length === 0) {
    return (
      <SkyText size={200} testId={`${regionId}-tests-all-passed`}>
        {`All ${testCount} ${testCount === 1 ? "test" : "tests"} passed.`}
      </SkyText>
    );
  }
  return (
    <section aria-label={`Failed and warned tests of run ${row.runId}`}>
      <SkyDataGrid<RunTransformTestResult>
        data={problemTests}
        columns={testColumns}
        getRowId={(test) => test.node}
      />
    </section>
  );
}

/** Commit and Open run (#1207 round 1, #1191 rule: no exit class or image tag). */
function RunFooter({ row, regionId }: { row: DbtJobRunRow; regionId: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`${regionId}-footer`}>
      <SkyText size={200} color="muted" testId={`${regionId}-commit`}>
        {"Commit "}
        {row.commit ? (
          <span className="font-mono" title={row.commit}>
            {row.commit.slice(0, 7)}
          </span>
        ) : (
          "not recorded"
        )}
      </SkyText>
      <Link
        href={absolutePath(`/runs/${row.runId}`)}
        className="underline-offset-2 hover:underline"
        data-testid={`${regionId}-open`}
      >
        <SkyText size={200} color="accent">
          Open run
        </SkyText>
      </Link>
    </div>
  );
}

function ExpandedRun({
  row,
  onQuery,
}: {
  row: DbtJobRunRow;
  onQuery(target: TransformationSqlTarget): void;
}) {
  const results = useRunTransformResults(row.runId);
  // Owner 2026-10-14 (#1002): the Query dialog reads only for Tenant admins and above, and the
  // message line comes from that same read. Below it neither control is offered: Query is a dash
  // with the owner's sentence, and "View message" is left out (the Cause cell already shows the
  // cause) (review on #1027).
  const mayQuery = usePermissions().can("instance.binding.identity.read");
  // Round 7: the list shortened this row's selection; its details read the row whole, once.
  const whole = useDbtJobRun(row.selectionShortened ? row.runId : null);
  const regionId = `transformation-history-run-${row.runId}`;
  const outcome = runOutcome(row);

  const models = [...(results.data?.models ?? [])].sort(
    (a, b) => Number(b.failed) - Number(a.failed) || a.name.localeCompare(b.name),
  );
  const tests = results.data?.tests ?? [];
  const problemTests = tests
    .filter((test) => test.failed || test.status === "warn")
    .sort((a, b) => Number(b.failed) - Number(a.failed) || a.name.localeCompare(b.name));
  const okModels = models.filter((model) => !model.failed && model.status === "success").length;
  const failedModels = models.filter((model) => model.failed).length;
  // #1093: a view's null is no count, not 0; the same sum as Rows written (`sumRowCounts`).
  const rows = sumRowCounts(
    models.map((model) => dbtNodeRows(model.materialization, model.rowsAffected)),
  );

  const queryButton = (node: string, name: string, focus?: "message") =>
    SQL_KINDS.test(node) && !mayQuery ? (
      focus ? null : (
        <Dash
          label={`No query: ${SOURCE_SQL_TENANT_ADMIN_ONLY}`}
          hint={SOURCE_SQL_TENANT_ADMIN_ONLY}
        />
      )
    ) : SQL_KINDS.test(node) ? (
      <SkyButton
        appearance="subtle"
        size="small"
        aria-label={`${focus ? "View message of" : "Query of"} ${name}`}
        testId={`transformation-history-${focus ? "message" : "query"}-${name}`}
        onClick={(event) =>
          onQuery({
            runId: row.runId,
            nodeId: node,
            name,
            focus,
            opener: event.currentTarget as HTMLElement,
          })
        }
      >
        {focus ? "View message" : "Query"}
      </SkyButton>
    ) : (
      <SkyText size={200} color="muted" aria-label="No query for a seed or snapshot">
        —
      </SkyText>
    );

  const modelColumns: SkyDataGridColumn<RunTransformModelResult>[] = [
    {
      key: "name",
      header: "Model",
      width: "22%",
      renderCell: (_value, model) => (
        <SkyText size={200} font="monospace" truncate title={model.node}>
          {model.name}
        </SkyText>
      ),
    },
    {
      key: "status",
      header: "Result",
      width: "12%",
      renderCell: (_value, model) => {
        const word = lastResultWord(model.status);
        return (
          <StatusCell
            testId={`transformation-history-result-${model.name}`}
            badge={
              <SkyBadge appearance="tint" color={word.color}>
                {word.word}
              </SkyBadge>
            }
          />
        );
      },
    },
    {
      key: "rowsAffected",
      header: "Rows",
      width: "9%",
      align: "right",
      renderCell: (_value, model) =>
        model.rowsAffected === null ? (
          // Review nit H8: the Models view's tooltip, for a model that built without a count.
          <Dash
            label="Row count not reported"
            hint={model.status === "success" ? ROWS_HINT : undefined}
          />
        ) : (
          <SkyText size={200} font="numeric">
            {model.rowsAffected.toLocaleString()}
          </SkyText>
        ),
    },
    {
      key: "executionTimeSeconds",
      header: "Time",
      width: "8%",
      align: "right",
      renderCell: (_value, model) => (
        <SkyText size={200} font="numeric">
          {formatSeconds(model.executionTimeSeconds)}
        </SkyText>
      ),
    },
    {
      key: "materialization",
      header: "Built as",
      width: "9%",
      renderCell: (_value, model) => (
        <SkyText size={200} font="monospace">
          {model.materialization ?? "—"}
        </SkyText>
      ),
    },
    {
      key: "cause",
      header: "Cause",
      width: "28%",
      renderCell: (_value, model) =>
        model.cause ? (
          <div className="flex flex-col gap-1">
            <CauseBlock
              whyOnlyWhenGeneric
              cause={model.cause}
              compact
              testId={`transformation-history-cause-${model.name}`}
            />
            {queryButton(model.node, model.name, "message")}
          </div>
        ) : (
          <SkyText size={200} color="muted">
            —
          </SkyText>
        ),
    },
    {
      key: "query",
      header: "Query",
      width: "12%",
      renderCell: (_value, model) => queryButton(model.node, model.name),
    },
  ];

  const testColumns: SkyDataGridColumn<RunTransformTestResult>[] = [
    {
      key: "name",
      header: "Test",
      width: "24%",
      renderCell: (_value, test) => (
        <SkyText size={200} font="monospace" truncate title={test.node}>
          {test.name}
        </SkyText>
      ),
    },
    {
      key: "model",
      header: "Model",
      width: "14%",
      renderCell: (_value, test) => (
        <SkyText size={200} font="monospace" truncate>
          {test.model ? (test.model.split(".").at(-1) ?? test.model) : "—"}
        </SkyText>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "11%",
      renderCell: (_value, test) => (
        <SkyText size={200}>
          {testKindWord({ kind: test.kind ?? undefined, uniqueId: test.node })}
        </SkyText>
      ),
    },
    {
      key: "failures",
      header: "Failures",
      width: "9%",
      align: "right",
      renderCell: (_value, test) => (
        <SkyText size={200} font="numeric">
          {test.failures === null
            ? "—"
            : `${test.failures} ${failuresUnit({ cause: test.cause ?? undefined, kind: test.kind ?? undefined }, test.failures)}`}
        </SkyText>
      ),
    },
    {
      key: "cause",
      header: "Cause",
      width: "28%",
      renderCell: (_value, test) => (
        <div className="flex flex-col gap-1">
          {test.cause ? (
            <CauseBlock
              whyOnlyWhenGeneric
              cause={test.cause}
              compact
              testId={`transformation-history-test-cause-${test.name}`}
            />
          ) : (
            <SkyBadge appearance="tint" color={test.failed ? "danger" : "warning"}>
              {test.failed ? "Failed" : "Warned"}
            </SkyBadge>
          )}
          {queryButton(test.node, test.name, "message")}
        </div>
      ),
    },
    {
      key: "query",
      header: "Query",
      width: "12%",
      renderCell: (_value, test) => queryButton(test.node, test.name),
    },
  ];

  return (
    <section
      id={regionId}
      aria-label={`Run ${row.runId} details`}
      className="flex flex-col gap-3 rounded-lg border border-border bg-muted p-3"
      data-testid={regionId}
    >
      {row.selectionShortened ? (
        <WholeSelection row={row} whole={whole} regionId={regionId} />
      ) : null}
      {results.isPending ? <LoadingRows rows={3} columns={5} /> : null}
      {results.isError ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="We could not read this run's models and tests"
          message={results.error instanceof Error ? results.error.message : "Something went wrong."}
          actions={
            <SkyButton
              appearance="secondary"
              size="small"
              icon={RetryIcon}
              onClick={() => void results.refetch()}
            >
              Retry
            </SkyButton>
          }
        />
      ) : null}
      {results.data ? (
        <>
          <SkyCard className="bg-card p-3" testId={`${regionId}-models`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <SkyText size={300} weight="semibold">
                Models
              </SkyText>
              <SkyText size={200} color="muted" testId={`${regionId}-models-summary`}>
                {`${okModels} built · ${failedModels} failed · ${rows === null ? "No row count" : formatRows(rows)}`}
              </SkyText>
              <Link
                href={absolutePath(`/runs/${row.runId}?view=events&step=transform`)}
                className="ml-auto inline-flex items-center gap-1 underline-offset-2 hover:underline"
                data-testid={`${regionId}-model-logs`}
              >
                <LogsIcon className="h-4 w-4" />
                <SkyText size={200} color="accent">
                  View model logs
                </SkyText>
              </Link>
            </div>
            {models.length === 0 ? (
              <SkyText size={200} color="muted">
                {noModelsSentence(outcome, row)}
              </SkyText>
            ) : (
              <section aria-label={`Models of run ${row.runId}`}>
                <SkyDataGrid<RunTransformModelResult>
                  data={models}
                  columns={modelColumns}
                  getRowId={(model) => model.node}
                  pagination={models.length > 25}
                  pageSize={25}
                />
              </section>
            )}
          </SkyCard>

          <SkyCard className="bg-card p-3" testId={`${regionId}-tests`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <SkyText size={300} weight="semibold">
                Tests
              </SkyText>
              <SkyText size={200} color="muted" testId={`${regionId}-tests-summary`}>
                {testsLine(row)}
              </SkyText>
            </div>
            <RunTestsBody
              row={row}
              regionId={regionId}
              testCount={tests.length}
              testsRequested={results.data?.testsRequested}
              problemTests={problemTests}
              testColumns={testColumns}
            />
          </SkyCard>
        </>
      ) : null}

      <SkyText size={200} testId={`${regionId}-setup`}>
        {"Setup: "}
        {setupText(row, outcome)}
      </SkyText>

      <RunFooter row={row} regionId={regionId} />
    </section>
  );
}

// `canStart` is part of the view contract (contract §6.10); nothing in History writes.
/**
 * One segment of the runs (`historySegments`). Review r1 S1: below its floor (1128 px, so at
 * 1280 px) the grid scrolls, and Status, the run's outcome, is the last column, so it is pinned
 * (`grid-pinned-last`), as Actions is on the Runs list. Each segment scrolls on its own, so each
 * keeps its own edge line.
 */
function HistorySegmentGrid({
  rows,
  columns,
  testId,
}: {
  rows: DbtJobRunRow[];
  columns: SkyDataGridColumn<DbtJobRunRow>[];
  testId: string;
}) {
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(true);
  return (
    <div ref={pinnedEdge}>
      <SkyDataGrid<DbtJobRunRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.runId}
        className="grid-pinned-last [&_table]:min-w-(--transformation-history-floor)"
        testId={testId}
      />
    </div>
  );
}

/** A failed read of the runs, with Retry. */
function RunsReadError({
  title,
  error,
  testId,
  onRetry,
}: {
  title: string;
  error: unknown;
  testId: string;
  onRetry: () => void;
}) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title={title}
      message={error instanceof Error ? error.message : "Something went wrong."}
      testId={testId}
      actions={
        <SkyButton appearance="secondary" size="small" icon={RetryIcon} onClick={onRetry}>
          Retry
        </SkyButton>
      }
    />
  );
}

/** Load more; a click while the page loads does nothing. */
function LoadMoreRuns({ loading, onLoadMore }: { loading: boolean; onLoadMore: () => void }) {
  return (
    <SkyButton
      appearance="secondary"
      size="small"
      aria-busy={loading}
      onClick={() => {
        if (loading) return;
        onLoadMore();
      }}
      testId="transformation-history-load-more"
    >
      {loading ? "Loading more runs…" : "Load more"}
    </SkyButton>
  );
}

export function TransformationHistoryView(_props: TransformationHistoryViewProps) {
  const notice = useNotice();
  const runs = useDbtJobRunPages(PAGE_SIZE);
  const refreshFirstPage = useRefreshFirstRunPage(PAGE_SIZE);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [sqlTarget, setSqlTarget] = useState<TransformationSqlTarget | null>(null);
  const toggles = useRef(new Map<string, HTMLButtonElement>());
  // Review S5: the loaded pages stay on screen (grid, expanded regions, the focused Load more)
  // while the next one loads; round 7 keeps that with an infinite query, which appends.
  const loadingMore = runs.isFetchingNextPage;
  const loadMoreClicked = useRef(false);
  const countLine = useRef<HTMLDivElement>(null);

  const rows = runs.data?.pages.flatMap((page) => page.rows) ?? [];
  const lastPageLoaded = Boolean(runs.data) && !runs.hasNextPage;
  useEffect(() => {
    if (!loadMoreClicked.current || !lastPageLoaded) return;
    loadMoreClicked.current = false;
    countLine.current?.focus();
  }, [lastPageLoaded]);
  const newest = rows[0] ?? null;
  usePollWhile(Boolean(newest && isRunInFlight(newest)), RUN_POLL_INTERVAL_MS, () => {
    // Review r2: one request a poll, the first page; a page Load more is reading would overwrite
    // the merge, so the poll waits for it. A failed poll leaves the rows; the next one tries again.
    if (runs.isFetchingNextPage) return;
    refreshFirstPage().catch(() => undefined);
  });

  const toggle = (runId: string): void =>
    setExpanded((open) =>
      open.includes(runId) ? open.filter((id) => id !== runId) : [...open, runId],
    );

  const copy = async (runId: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(runId);
      notice.info("Run id copied", { forPath: ROUTES.transformations });
    } catch {
      notice.info(`Run id ${runId}: copy it from the screen.`, {
        forPath: ROUTES.transformations,
      });
    }
  };

  const columns: SkyDataGridColumn<DbtJobRunRow>[] = [
    {
      key: "expand",
      header: "Details",
      width: `${DETAILS_WIDTH}px`,
      renderCell: (_value, row) => {
        const open = expanded.includes(row.runId);
        return (
          <SkyButton
            ref={(element: HTMLButtonElement | null) => {
              if (element) toggles.current.set(row.runId, element);
              else toggles.current.delete(row.runId);
            }}
            appearance="subtle"
            size="small"
            icon={open ? ChevronDownIcon : ChevronRightIcon}
            aria-expanded={open}
            aria-controls={open ? `transformation-history-run-${row.runId}` : undefined}
            aria-label={`${open ? "Hide" : "Show"} details of run ${row.runId}`}
            testId={`transformation-history-expand-${row.runId}`}
            onClick={() => toggle(row.runId)}
          />
        );
      },
    },
    {
      key: "runId",
      header: "Run",
      width: `${RUN_ID_WIDTH}px`,
      // Round 3 (7ipw67.png): the id was an inline `truncate` (which an inline element ignores),
      // so it ran on under the copy button. It now wraps after its second hyphen, two lines at
      // most, and the button keeps its own width beside it.
      renderCell: (_value, row) => (
        <div className="flex min-w-0 items-center gap-1">
          <Link
            href={absolutePath(`/runs/${row.runId}`)}
            className="min-w-0 underline-offset-2 hover:underline"
          >
            <SkyText size={200} font="monospace" color="accent" title={row.runId}>
              {row.runId}
            </SkyText>
          </Link>
          <SkyButton
            appearance="subtle"
            size="small"
            icon={CopyIcon}
            aria-label="Copy run id"
            className="shrink-0"
            testId={`transformation-history-copy-${row.runId}`}
            onClick={() => void copy(row.runId)}
          />
          {row.attempt > 1 ? (
            <span
              className="shrink-0"
              data-testid={`transformation-history-attempt-${row.runId}`}
              title="This run was retried. The times and the result are for the latest attempt."
            >
              <SkyBadge appearance="tint" color="informative">
                {`Attempt ${row.attempt}`}
              </SkyBadge>
            </span>
          ) : null}
        </div>
      ),
    },
    {
      // gridsfit: the start over its duration (`StartedCell`); there is no Duration column.
      key: "startedAt",
      header: "Started",
      width: `${STARTED_WIDTH}px`,
      renderCell: (_value, row) =>
        // #986: a queued run has not started, so it reads so and shows no time or duration.
        runNotStarted(row) ? (
          <SkyText
            size={200}
            color="muted"
            block
            testId={`transformation-history-not-started-${row.runId}`}
          >
            {NOT_STARTED_LABEL}
          </SkyText>
        ) : // W6: with both durations known, "Run 1m 14s" over "dbt step 1m 04s" (the id the dbt
        // step's value always had stays on it); the dbt step's alone otherwise, as before, so a
        // skipped or never-started step reads a dash, never the run's duration (review r1 B1).
        // #1214: the time is the run's start, the one the Runs list shows.
        isKnownSeconds(row.runDurationSeconds) && isKnownSeconds(row.durationSeconds) ? (
          <StartedCell
            at={runStartOf(row)}
            durationSeconds={row.runDurationSeconds}
            durationTestId={`transformation-history-run-duration-${row.runId}`}
            dbtStep={{
              seconds: row.durationSeconds,
              testId: `transformation-history-duration-${row.runId}`,
            }}
          />
        ) : runStartOf(row) !== (row.startedAt ?? row.createdAt) &&
          isKnownSeconds(row.durationSeconds) ? (
          // The run is still open or was retried, so its start and the dbt step's differ and the
          // step's seconds are named as the step's own, never read as the run's (review r2). One
          // line under the time, "dbt step 1m 04s", never a "Duration not known" dash beside it,
          // and the old Duration cell's id on the value alone (round 3, A).
          <DbtStepStartedCell
            at={runStartOf(row)}
            seconds={row.durationSeconds}
            testId={`transformation-history-duration-${row.runId}`}
          />
        ) : (
          // The dbt step's duration pairs with the run's start only when both are one instant
          // (an older server, or a step that began with its run).
          <StartedCell
            at={runStartOf(row)}
            durationSeconds={
              runStartOf(row) === (row.startedAt ?? row.createdAt) ? row.durationSeconds : null
            }
            durationTestId={`transformation-history-duration-${row.runId}`}
          />
        ),
    },
    {
      key: "startedBy",
      header: "Started by",
      width: "12%",
      // The Runs list's cell (S11 PR review N3), so one run reads the same on both screens: the
      // label, or a dash with the reason in a tooltip and for screen readers. A transformation
      // run row carries no schedule id, so there is no "Run now of {schedule}" line here.
      renderCell: (_value, row) => (
        <div data-testid={`transformation-history-started-by-${row.runId}`}>
          <StartedByCell row={{ ...row, scheduleId: null }} />
        </div>
      ),
    },
    {
      key: "dbtJobNames",
      header: "dbt jobs",
      width: "13%",
      // Round 6 (T3 with T1): a run of chosen models reads its selection under "Chosen models",
      // so a queued one says what it will build before dbt starts.
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          <SkyText
            size={200}
            block
            truncate
            title={row.dbtJobNames.join(", ")}
            testId={`transformation-history-names-${row.runId}`}
          >
            {row.dbtJobNames.length > 0 ? row.dbtJobNames.join(", ") : "—"}
          </SkyText>
          {row.adhocSelect ? (
            <SkyText
              size={200}
              color="muted"
              font="monospace"
              block
              truncate
              title={row.adhocSelect}
              testId={`transformation-history-selection-${row.runId}`}
            >
              {row.adhocSelect}
            </SkyText>
          ) : null}
        </div>
      ),
    },
    {
      key: "modelsReported",
      header: "Models",
      width: "10%",
      renderCell: (_value, row) => {
        const cell = modelsCell(row);
        if (!cell) {
          return (
            <SkyText size={200} color="muted">
              —
            </SkyText>
          );
        }
        return (
          <SkyText size={200} testId={`transformation-history-models-${row.runId}`}>
            {cell.text}
            {cell.failed > 0 ? (
              <span className="text-destructive">{` · ${cell.failed} failed`}</span>
            ) : null}
            {cell.skipped > 0 ? <span>{` · ${cell.skipped} skipped`}</span> : null}
          </SkyText>
        );
      },
    },
    {
      key: "tests",
      header: "Tests",
      width: "12%",
      renderCell: (_value, row) => (
        <SkyText size={200} testId={`transformation-history-tests-${row.runId}`}>
          {testsLine(row)}
        </SkyText>
      ),
    },
    {
      key: "stepStatus",
      header: "Status",
      width: `${STATUS_WIDTH}px`,
      renderCell: (_value, row) => {
        const outcome = runOutcome(row);
        return (
          <StatusCell
            badge={
              <SkyBadge
                appearance="tint"
                color={outcome.color}
                testId={`transformation-history-status-${row.runId}`}
              >
                {outcome.word}
              </SkyBadge>
            }
          />
        );
      },
    },
  ];

  // The skeleton is for the first read only: a Load more keeps the rows it has (review S5).
  if (runs.isPending) {
    return (
      <div data-testid="transformation-history-loading">
        <LoadingRows rows={6} columns={8} />
      </div>
    );
  }
  if (runs.isError && rows.length === 0) {
    return (
      <div data-testid="transformation-history-view">
        <RunsReadError
          title="We could not read the transformation runs."
          error={runs.error}
          testId="transformation-history-error"
          onRetry={() => void runs.refetch()}
        />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="transformation-history-view">
        <SkyText size={200} color="muted" testId="transformation-history-empty">
          No transformation run yet.
        </SkyText>
      </div>
    );
  }

  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  // Round 7: the server says whether there is a next page (its cursor); there is no cap.
  const canLoadMore = runs.hasNextPage;
  const segments = historySegments(rows, expanded);

  return (
    <div className="flex flex-col gap-3" data-testid="transformation-history-view">
      <section
        aria-label="Transformation runs, newest first"
        className="flex flex-col gap-3"
        style={{ "--transformation-history-floor": `${layout.floor}px` } as CSSProperties}
      >
        {segments.map((segment, index) => {
          const first = segment[0]!;
          const last = segment[segment.length - 1]!;
          return (
            <Fragment key={first.runId}>
              <HistorySegmentGrid
                rows={segment}
                columns={layout.columns}
                testId={
                  index === 0
                    ? "transformation-history-grid"
                    : `transformation-history-grid-${first.runId}`
                }
              />
              {expanded.includes(last.runId) ? (
                <ExpandedRun row={last} onQuery={setSqlTarget} />
              ) : null}
            </Fragment>
          );
        })}
      </section>

      {runs.isError && rows.length > 0 ? (
        <RunsReadError
          title="We could not read more transformation runs."
          error={runs.error}
          testId="transformation-history-more-error"
          onRetry={() => void (runs.isFetchNextPageError ? runs.fetchNextPage() : runs.refetch())}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div ref={countLine} tabIndex={-1} className="outline-none">
          <SkyText size={200} color="muted" testId="transformation-history-count">
            {`Showing the last ${rows.length} ${rows.length === 1 ? "run" : "runs"}.`}
          </SkyText>
        </div>
        {canLoadMore ? (
          // Never `disabled` while loading: a disabled button drops the keyboard focus. A second
          // click while the page loads does nothing.
          <LoadMoreRuns
            loading={loadingMore}
            onLoadMore={() => {
              loadMoreClicked.current = true;
              // After a failed page the button asks for that page again, never the one after it
              // (review r2 nit 5): the cursor of a failed page is not advanced. The focus stays.
              void runs.fetchNextPage();
            }}
          />
        ) : null}
      </div>

      {sqlTarget ? (
        <TransformationSqlDialog target={sqlTarget} onClose={() => setSqlTarget(null)} />
      ) : null}
    </div>
  );
}

/**
 * The Started cell of a run whose start is not its dbt step's and whose run duration is not
 * known: the run's start (#1214), then one line naming the dbt step's own duration.
 */
function DbtStepStartedCell({
  at,
  seconds,
  testId,
}: {
  at: string | null | undefined;
  seconds: number;
  testId: string;
}) {
  const zone = useDisplayTz();
  return (
    <div className="flex min-w-0 flex-col">
      <SkyText size={200} block title={instantHoverTitle(at, zone)}>
        {formatInstant(at, zone, "seconds")}
      </SkyText>
      <SkyText size={200} color="muted" font="numeric" block>
        {"dbt step "}
        <span data-testid={testId}>{formatDuration(seconds)}</span>
      </SkyText>
    </div>
  );
}
